import { MobileNavigationTabBar } from '@src/components/Mobile/TabBar/MobileNavigationTabBar';
import { useAppLayoutForRouteParams, useCanonicalLayoutIdForSection } from '@src/hooks/useAppLayoutId';
import { useRedirectSpacesRoute } from '@src/hooks/useSpaceSectionRedirectLocation';
import { useSpacesRouting } from '@src/hooks/useSpacesRouting';
import { useAppLayoutConfig } from '@src/providers/AppLayoutConfig/AppLayoutConfigContext';
import { MobileLayoutPanelProvider } from '@src/providers/MobileLayoutPanelProvider/MobileLayoutPanelProvider';
import type { NavigationItem } from '@src/tokens/navigationPaths';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { MobileHeader } from '../../../components/Mobile/MobileHeader/MobileHeader';
import { BottomScrollGradient, TopScrollGradient } from '../../../components/Mobile/styles';
import { NoMatchPage } from '../../NoMatchPage';
import { MobileTradingPanelDispatcher } from './MobileTradingPanelDispatcher';
import { TradingPlaceholder } from './TradingPlaceholder';
import { useMobileTradingNavItems } from './useMobileTradingNavItems';
import { getRedirectToDefaultChildPath } from './utils/getRedirectToDefaultChildPath';

const LAYOUT_SECTION = 'trading' as const;

/**
 * Top-level route for `/trading/*` on mobile. Matches both URL shapes — `/trading/:layoutId/*` and
 * `/trading/:navGroupName/:layoutId/*` — and redirects bare `/trading` to the user's canonical space path.
 * Mirrors the desktop `TradingRoute` shell so both platforms share URL semantics.
 */
export function MobileTradingRoute() {
  const canonicalLayoutId = useCanonicalLayoutIdForSection(LAYOUT_SECTION);
  const { navGroupNames, indexRedirectPath } = useSpacesRouting(LAYOUT_SECTION, canonicalLayoutId);

  return (
    <Routes>
      <Route index element={<Navigate to={indexRedirectPath} replace />} />

      {navGroupNames.map(navGroupName => (
        <Route
          key={navGroupName}
          path={`${navGroupName}/:layoutId/*`}
          element={<MobileTradingLayout navGroupName={navGroupName} />}
        />
      ))}

      <Route path=":layoutId/*" element={<MobileTradingLayout />} />

      <Route path="*" element={<NoMatchPage />} />
    </Routes>
  );
}

interface MobileTradingLayoutProps {
  /** Set when matched under `/trading/:navGroupName/:layoutId/*`; omitted for `/trading/:layoutId/*`. */
  navGroupName?: string;
}

/**
 * Mobile trading layout shell for both URL shapes: `/trading/:layoutId/*` and `/trading/:navGroupName/:layoutId/*`.
 * Mirrors desktop `TradingSpaceAppLayout`: delegates unknown layout ids and stale/missing group slugs to
 * {@link useRedirectSpacesRoute} instead of silently substituting a layout.
 */
function MobileTradingLayout({ navGroupName }: MobileTradingLayoutProps) {
  const { layoutId } = useParams<{ layoutId?: string }>();
  const { layoutConfigsMap, selectedLayoutId, updateSelectedLayoutId } = useAppLayoutConfig(LAYOUT_SECTION);
  const spaceRedirect = useRedirectSpacesRoute(LAYOUT_SECTION, navGroupName);
  const tradingNavItems = useMobileTradingNavItems();

  useEffect(() => {
    if (layoutId == null || layoutConfigsMap?.[layoutId] == null || selectedLayoutId === layoutId) {
      return;
    }
    updateSelectedLayoutId(layoutId);
  }, [layoutId, layoutConfigsMap, selectedLayoutId, updateSelectedLayoutId]);

  if (spaceRedirect != null) {
    return <Navigate to={spaceRedirect} replace />;
  }

  return (
    <>
      <TopScrollGradient />
      <MobileHeader />
      <Routes>
        <Route index element={<TradingLayoutIndex tradingNavItems={tradingNavItems} />} />
        <Route path=":panelId/:tabId" element={<MobileTradingPanelOutlet />} />
        <Route path=":panelId" element={<MobileTradingPanelOutlet />} />
      </Routes>
      <BottomScrollGradient />
      <MobileNavigationTabBar navItems={tradingNavItems} layoutSectionLabel={TOP_LEVEL_NAV_LABEL} />
    </>
  );
}

/**
 * Rendered at the layout root (`/trading/:layoutId`). Redirects to the default child destination (first
 * market sub-panel or active panel) when one exists; otherwise shows the top-level placeholder.
 */
function TradingLayoutIndex({ tradingNavItems }: { tradingNavItems: NavigationItem[] }) {
  const location = useLocation();
  const childRedirectPath = getRedirectToDefaultChildPath(tradingNavItems, location.pathname);
  if (childRedirectPath != null) {
    return <Navigate to={childRedirectPath} replace />;
  }
  return <TradingPlaceholder label={TOP_LEVEL_NAV_LABEL} />;
}

/**
 * Resolves `:panelId` against the current layout and hands the panel to {@link MobileTradingPanelDispatcher},
 * which renders the correct mobile component for the panel's type. Falls back to the top-level placeholder
 * when the panel or layout cannot be resolved.
 */
function MobileTradingPanelOutlet() {
  const { panelId } = useParams<{ panelId: string }>();
  const layout = useAppLayoutForRouteParams(LAYOUT_SECTION);
  const panel = panelId != null ? layout?.dockViewLayout.panels[panelId] : undefined;

  if (layout == null || panel == null) {
    return <TradingPlaceholder label={TOP_LEVEL_NAV_LABEL} />;
  }

  return (
    <MobileLayoutPanelProvider>
      <MobileTradingPanelDispatcher layout={layout} panel={panel} />
    </MobileLayoutPanelProvider>
  );
}

export const TOP_LEVEL_NAV_LABEL = 'Trading';
